import { fromEntries } from '@/helpers/javascript'
import api from '@/api'
import {Module} from 'vuex'
import {Field, Rule, Ruleset} from '@/api/rules'

// initial state
const state = () => ({
	rules: {} as Record<string, Rule>,
	rulesets: {} as Record<string, Ruleset>,
	fields: {} as Record<string, Field>,
	rulesetName: undefined as string | undefined
})

type RulesState = ReturnType<typeof state>

const store: Module<RulesState, RulesState> = {
	namespaced: true,
	state,
	getters: {},
	actions: {
		async fetchRules ({ commit }) {
			const response = await api.rules.getRules()
			commit('SET_RULES', response.data)
		},

		async updateRule ({ commit }, rule: Rule) {
			const response = await api.rules.updateRule(rule)
			commit('SET_RULE', response.data)
		},

		async deleteRule ({ commit, dispatch }, rule: Rule) {
			await api.rules.deleteRule(rule)
			commit('DELETE_RULE', rule)
			await dispatch('fetchRulesets')
		},

		async fetchRulesets ({ commit }) {
			const response = await api.rules.getRulesets()
			commit('SET_RULESETS', response.data)
		},

		async updateRuleset ({ commit }, ruleset) {
			const response = await api.rules.updateRuleset(ruleset)
			const data = response.data;
			commit('SET_RULESET', data)
			return data
		},

		async deleteRuleset ({ commit, dispatch }, ruleset) {
			await api.rules.deleteRuleset(ruleset)
			commit('DELETE_RULESET', ruleset)
		},

		async includeRule ({ commit, dispatch }, { rulesetName, name }) {
			const response = await api.rules.include({ rulesetName, name })
			commit('SET_RULESET_RULES', { name: rulesetName, rules: response.data })
		},

		async replaceRule ({ commit, dispatch }, { rulesetName, name, value }) {
			const response = await api.rules.replace({ rulesetName, name, value })
			commit('SET_RULESET_RULES', { name: rulesetName, rules: response.data })
		},

		async fetchFields ({ commit }) {
			const response = await api.rules.getFields()
			commit('SET_FIELDS', response.data)
		},

		async updateField ({ commit }, field: Field) {
			const response = await api.rules.updateField(field)
			commit('SET_FIELD', response.data)
		},

		async deleteField ({ commit }, field: Field) {
			await api.rules.deleteField(field)
			commit('DELETE_FIELD', field)
		}
	},
	mutations: {
		SET_RULES (state, rules: Rule[]) {
			state.rules = fromEntries(rules.map(r => [r.name, r]))
		},

		SET_RULE (state, rule: Rule) {
			state.rules[rule.name] = rule
		},

		DELETE_RULE (state, rule: Rule) {
			delete state.rules[rule.name]
		},

		SET_RULESETS (state, rulesets: Ruleset[]) {
			state.rulesets = fromEntries(rulesets.map(r => [r.name, r]))
		},

		SET_RULESET (state, ruleset: Ruleset) {
			state.rulesets[ruleset.name] = ruleset
		},

		SET_RULESET_RULES (state, { name, rules }: Ruleset) {
			state.rulesets[name] = Object.assign({}, state.rulesets[name], { rules })
		},

		DELETE_RULESET (state, ruleset: Ruleset) {
			delete state.rulesets[ruleset.name]
		},

		SET_FIELDS (state, fields: Field[]) {
			state.fields = fromEntries(fields.map(f => [f.name, f]))
		},

		SET_FIELD (state, field: Field) {
			state.fields[field.name] = field
		},

		DELETE_FIELD (state, field: Field) {
			delete state.fields[field.name]
		},

		SET_RULESET_NAME (state, name: string | null) {
			if (name === null) {
				state.rulesetName = undefined
			} else {
				state.rulesetName = name
			}
		}
	}
}

export default store
